import { useEffect, useState } from "react";
import { Check } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { adminMediaUrl, api, mediaUrl } from "@/lib/api";
import { BAN_CATEGORIES, REPORT_REASONS } from "@/lib/constants";

export const ACTIONS = {
  dismiss: { label: "Dismiss", title: "Dismiss these reports?", desc: "No action on the account. Reporters hear that no action was needed.", destructive: false },
  warn: { label: "Warn", title: "Send a warning?", desc: "They get the warning message in the app. Reporters hear that action was taken.", destructive: false },
  hide: { label: "Hide", title: "Hide this profile?", desc: "They stop appearing in Discover until the hide ends or someone unhides them.", destructive: true },
  remove_photos: { label: "Remove photos", title: "Remove the selected photos?", desc: "If they drop below the photo minimum they're hidden until they add more.", destructive: true },
  ban: { label: "Ban", title: "Ban this account?", desc: "Blocks their phone, devices and face, ends their chats, and lets them appeal once.", destructive: true },
};

// Default ban category: the most serious open report reason (backend order), skipping "other".
const topReason = (reports) => Object.keys(REPORT_REASONS).find((k) => k !== "other" && reports.some((r) => r.reason === k)) || "other";

function photoChoices(group) {
  const seen = new Map();
  group.reports.forEach((r) => (r.snapshot?.photos || []).forEach((p) => { if (!seen.has(p.id)) seen.set(p.id, adminMediaUrl(p.url)); }));
  if (!seen.size && group.reported.photo) seen.set("main", mediaUrl(group.reported.photo));
  return [...seen.entries()];
}

export default function ReportActionDialog({ action, group, onOpenChange, onDone }) {
  const [reason, setReason] = useState("");
  const [days, setDays] = useState("");
  const [photos, setPhotos] = useState([]);
  const [category, setCategory] = useState("other");
  useEffect(() => {
    if (!action) return;
    setReason(""); setDays(""); setPhotos([]); setCategory(topReason(group.reports));
  }, [action, group]);
  const cfg = ACTIONS[action] || {};
  const choices = action === "remove_photos" ? photoChoices(group) : [];
  const valid = reason.trim().length >= 3 && (action !== "remove_photos" || photos.length > 0) && (days === "" || (+days >= 1 && +days <= 3650));

  const run = async () => {
    if (!valid) throw new Error(reason.trim().length < 3 ? "A reason of at least 3 characters is required." : "Pick at least one photo.");
    const body = { action, reason: reason.trim() };
    if (action === "hide" && days) body.days = +days;
    if (action === "remove_photos") body.photo_ids = photos;
    if (action === "ban") body.category = category;
    onDone(action, await api(`/admin/reports/${group.reported.id}/action`, { body }));
  };

  return (
    <ConfirmDialog open={!!action} onOpenChange={onOpenChange} testId="report-action-dialog" destructive={cfg.destructive}
      title={`${cfg.title || ""}${group?.reported.first_name ? ` (${group.reported.first_name})` : ""}`}
      description={cfg.desc} confirmLabel={cfg.label} onConfirm={run}>
      <div className="space-y-4">
        {action === "hide" && (
          <div><Label htmlFor="ra-days">Hide for (days)</Label>
            <Input id="ra-days" inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ""))} placeholder="Blank = until unhidden" className="mt-2" data-testid="report-action-days-input" /></div>
        )}
        {action === "ban" && (
          <div><Label htmlFor="ra-cat">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="ra-cat" className="mt-2" data-testid="report-action-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(BAN_CATEGORIES).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`report-action-category-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
        {action === "remove_photos" && (
          <fieldset><legend className="text-sm font-medium">Photos to remove ({photos.length} selected)</legend>
            <div className="mt-2 grid grid-cols-4 gap-2" data-testid="report-action-photo-picker">
              {choices.map(([id, url]) => {
                const on = photos.includes(id);
                return (
                  <button key={id} type="button" aria-pressed={on} onClick={() => setPhotos((p) => (on ? p.filter((x) => x !== id) : [...p, id]))}
                    className={`relative overflow-hidden rounded-xl ring-2 transition-all ${on ? "ring-destructive" : "ring-transparent"}`} data-testid={`report-action-photo-${id}`}>
                    <img src={url} alt="Snapshot" className="aspect-square w-full object-cover" />
                    {on && <span className="absolute right-1 top-1 rounded-full bg-destructive p-0.5 text-white"><Check size={12} /></span>}
                  </button>
                );
              })}
            </div></fieldset>
        )}
        <div><Label htmlFor="ra-reason">Reason (required)</Label>
          <Textarea id="ra-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What you saw and why. Recorded in the admin log." className="mt-2" data-testid="report-action-reason-input" />
          {reason && reason.trim().length < 3 && <p className="cw-error mt-1 text-xs">At least 3 characters.</p>}</div>
      </div>
    </ConfirmDialog>
  );
}
